import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Sparkles, UtensilsCrossed, ShoppingBag, Bike } from "lucide-react";
import { BRAND, HERO_IMAGES } from "../data/restaurant";
import introBurst from "../assets/intro/intro-fireburst.jpg";
import logo from "../assets/pattista-logo.png";

const reveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Starburst = ({ className = "", children }) => {
  const points = Array.from({ length: 24 }, (_, i) => {
    const r = i % 2 === 0 ? 50 : 38;
    const a = (Math.PI * i) / 12;
    return `${50 + r * Math.cos(a)},${50 + r * Math.sin(a)}`;
  }).join(" ");
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 100 100" className="h-full w-full drop-shadow-[4px_4px_0_rgba(23,18,15,1)]" aria-hidden="true">
        <polygon points={points} fill="#F02F25" stroke="#17120F" strokeWidth="2.5" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
};

export default function Hero() {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 16 });
  const sy = useSpring(my, { stiffness: 60, damping: 16 });
  const rotateY = useTransform(sx, (v) => (reduce ? 0 : v * 8));
  const rotateX = useTransform(sy, (v) => (reduce ? 0 : -v * 8));
  const driftX = useTransform(sx, (v) => (reduce ? 0 : v * 14));
  const driftY = useTransform(sy, (v) => (reduce ? 0 : v * 14));

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const lines = ["WARNING:", "Your cravings", "are about to", "get serious"];

  return (
    <section id="top" data-testid="hero-section" onMouseMove={onMove} className="grain relative overflow-hidden bg-gradient-to-br from-sunshine via-amber to-ochre">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-cream/30 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-20 bottom-40 h-80 w-80 rounded-full bg-flame/20 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-4 z-0 -translate-x-1/2 lg:left-[80%]">
        <div className="flame-halo h-44 w-44 rounded-full bg-[radial-gradient(circle,rgba(255,201,40,0.95)_0%,rgba(240,47,37,0.55)_42%,rgba(217,154,25,0.25)_62%,transparent_78%)] blur-md sm:h-60 sm:w-60" />
      </div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-28 pt-14 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:gap-8 lg:pb-36 lg:pt-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border-2 border-jet bg-cream px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-crimson shadow-hard-sm sm:text-sm"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Burgers × Pasta
          </motion.p>

          <h1 data-testid="hero-title-reveal" className="font-drip text-[1.4rem] leading-[1.5] sm:text-[2.3rem] sm:leading-[1.5] lg:text-[2.8rem] lg:leading-[1.5]">
            {lines.map((line, li) => (
              <span key={li} className="block overflow-hidden pb-4">
                <motion.span
                  className="block will-change-transform"
                  custom={li}
                  variants={reveal}
                  initial="hidden"
                  animate="show"
                  aria-label={line}
                  data-testid={li === 0 ? "hero-warning-text" : undefined}
                >
                  {line.split(" ").map((word, wi) => (
                    <span key={wi} aria-hidden="true" className="inline-block whitespace-nowrap">
                      {word.split("").map((ch, i) => (
                        <span
                          key={i}
                          className={`drip-bob text-drip-fusion`}
                          style={{ animationDelay: `${((li * 5 + wi * 3 + i) % 12) * 0.11}s` }}
                        >
                          {ch}
                        </span>
                      ))}
                      {wi < line.split(" ").length - 1 && "\u00A0"}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.6 }}
            className="mt-5 text-lg font-extrabold leading-snug text-jet sm:text-xl"
            data-testid="hero-supporting-text"
          >
            Best <span className="text-crimson">PATTIes</span> and <span className="text-crimson">paSTAs</span> in the town!
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-3 flex items-center gap-2 font-hand text-3xl font-bold text-jet sm:text-4xl"
            data-testid="hero-slogan-accent"
          >
            {BRAND.sloganAccent}
            <Heart className="h-7 w-7 fill-crimson text-crimson" aria-hidden="true" />
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <Link
              to="/menu"
              data-testid="hero-cta-explore-menu"
              className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-jet px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-cream shadow-hard-flame transition-all duration-200 hover:-translate-y-1 hover:shadow-[10px_10px_0_0_#D92124] sm:text-base"
            >
              <UtensilsCrossed className="h-5 w-5" aria-hidden="true" />
              Explore the Menu
            </Link>
            <Link
              to="/menu"
              data-testid="hero-cta-order-pickup"
              className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-crimson px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-cream shadow-hard-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-hard sm:text-base"
            >
              <ShoppingBag className="h-5 w-5" aria-hidden="true" />
              Order for Pickup
            </Link>
            <a
              href="#delivery"
              data-testid="hero-cta-order-delivery"
              className="inline-flex items-center justify-center gap-2 rounded-full border-[3px] border-jet bg-cream px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-jet shadow-hard transition-all duration-200 hover:-translate-y-1 hover:shadow-hard-lg sm:text-base"
            >
              <Bike className="h-5 w-5" aria-hidden="true" />
              Order Delivery
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="mt-6 max-w-md text-sm font-semibold text-jet/70"
          >
            {BRAND.priceNote}
          </motion.p>
        </div>

        <motion.div
          style={{ perspective: 1000 }}
          className="relative mx-auto flex h-[420px] w-full max-w-md items-center justify-center sm:h-[520px] lg:max-w-none"
        >
          <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative h-full w-full">
            <motion.div style={{ x: driftX, y: driftY }} className="absolute left-1/2 top-0 z-20 -translate-x-1/2">
            <motion.img
              src={logo}
              alt="Pattista — flaming burger and pasta emblem"
              data-testid="hero-burning-logo"
              className="burning-logo relative w-40 sm:w-52"
              {...(!reduce && {
                animate: { y: [0, -14, 0] },
                transition: { duration: 5.5, repeat: Infinity, ease: "easeInOut" },
              })}
            />
          </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -5 }}
              transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-[4%] top-[22%] z-10 w-52 overflow-hidden rounded-3xl border-4 border-jet shadow-hard sm:w-64"
              data-testid="hero-card-burger"
            >
              <img src={HERO_IMAGES.burger.img} alt={HERO_IMAGES.burger.alt} className="aspect-square w-full object-cover" loading="eager" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 8 }}
              animate={{ opacity: 1, y: 0, rotate: 6 }}
              transition={{ delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-[3%] top-[46%] z-10 w-44 overflow-hidden rounded-3xl border-4 border-jet shadow-hard sm:w-56"
              data-testid="hero-card-pasta"
            >
              <img src={HERO_IMAGES.pasta.img} alt={HERO_IMAGES.pasta.alt} className="aspect-square w-full object-cover" loading="eager" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, duration: 0.5, type: "spring", bounce: 0.5 }}
              className="absolute bottom-[2%] left-[2%] z-20 h-28 w-28 sm:h-32 sm:w-32"
            >
              <Starburst>
                <span className="font-display text-sm font-black uppercase leading-tight text-cream">$10</span>
                <span className="font-display text-[10px] font-extrabold uppercase text-cream">Takeout</span>
                <span className="font-display text-[10px] font-extrabold uppercase text-cream">Deals</span>
              </Starburst>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-1/2 w-full -translate-x-1/2 select-none overflow-hidden">
        <p className="text-outline-jet whitespace-nowrap text-center font-display text-[19vw] font-black uppercase leading-[0.8] opacity-25">PATTISTA</p>
      </div>
    </section>
  );
}
